import { FieldItemList, CommonCase, CommonCaseTask } from 'models';
import { CaseLevel1Code } from 'shared/constants/common-cases';

import { useTranslation } from 'react-i18next';

import {
  CheckIcon,
  ClearIcon,
  PropertyLi,
  PropertyItem,
  PropertyTitle,
  PropertyValue,
} from 'components';
import safeParseHtml from 'shared/utils/safeParseHtml';

import { styled } from '@material-ui/core';

interface CommonCaseFields<T extends CommonCase> {
  otherInfo: FieldItemList<T>[];
  taskInfo: FieldItemList<T>[];
}

const CaseLevelItem = styled('span')({
  display: 'block',
  marginBottom: 10,
  '&:last-child': {
    marginBottom: 0,
  },
});

const StyledPropertyValue = styled(PropertyValue)(() => ({
  flexDirection: 'column',
}));

const Description = styled('span')(({ theme }) => ({
  fontSize: 14,
  color: theme.custom.darkBlue,
  whiteSpace: 'pre-line',
}));

const Column = styled(PropertyLi)(() => ({
  flexDirection: 'column',
  marginBottom: 10,
  '&:last-child': {
    marginBottom: 0,
  },
}));

const Item = styled(PropertyItem)(() => ({
  marginBottom: 0,
  fontSize: 14,
}));

const showEnteringSection = (commonCase: CommonCase) =>
  commonCase.CaseLevel1?.Value === CaseLevel1Code.FlawNotification;

interface TaskInfoProps {
  task: CommonCaseTask;
  idx: number;
}

const TaskInfo: React.FC<TaskInfoProps> = ({ task, idx }) => {
  const { t } = useTranslation();

  return (
    <Column key={idx}>
      <Item>
        <PropertyTitle>{task.Executor?.Name}</PropertyTitle>
        {<PropertyValue>{task.StatusInfo || task.Status?.Label}</PropertyValue>}
      </Item>
      {task.PMCompany?.Name && <Item>{task.PMCompany.Name}</Item>}
      {task.IsAutomaticallyManaged ? (
        <Item>
          <PropertyTitle>
            {t('#portal.incident.task.common.automaticallymanaged')}
          </PropertyTitle>
        </Item>
      ) : null}
    </Column>
  );
};

export const commonCaseFields: CommonCaseFields<CommonCase> = {
  otherInfo: [
    {
      id: 'CreatedOn',
      format: 'date',
      key: '##attribute#incident#createdon',
    },
    {
      id: 'ModifiedOn',
      format: 'date',
      key: '##attribute#incident#modifiedon',
    },
    {
      id: 'Space.Name',
      format: 'string',
      key: '#portal.uds_space.label.uds_name',
    },
    {
      id: 'CaseLevel1',
      format: 'string',
      key: '##attribute#incident#uds_requesttype',
      renderFullItem: (commonCase) => {
        return (
          <PropertyLi>
            <PropertyItem>
              <PropertyTitle>Palvelupyynnön tyyppi</PropertyTitle>
              <StyledPropertyValue>
                {commonCase.CaseLevel1 && (
                  <CaseLevelItem>{commonCase.CaseLevel1.Label}</CaseLevelItem>
                )}
                {commonCase.CaseLevel2 && (
                  <CaseLevelItem>{commonCase.CaseLevel2.Label}</CaseLevelItem>
                )}
                {commonCase.CaseLevel3 && (
                  <CaseLevelItem>{commonCase.CaseLevel3.Label}</CaseLevelItem>
                )}
              </StyledPropertyValue>
            </PropertyItem>
          </PropertyLi>
        );
      },
    },
    {
      id: 'Cooperative.Name',
      format: 'string',
      key: '##attribute#uds_space#uds_cooperativeid',
    },
    {
      id: 'IsMasterKeyAllowed',
      format: 'boolean',
      key: '##attribute#incident#uds_masterkeyallowed',
      variants: [<CheckIcon />, <ClearIcon />],
      header: {
        position: 'before',
        text: 'Ohjeet',
      },
      show: showEnteringSection,
    },
    {
      id: 'IsAppartmentHasAdditionalLock',
      format: 'boolean',
      key: '##attribute#incident#uds_appartmenthasadditionallock',
      variants: [<CheckIcon />, <ClearIcon />],
      show: showEnteringSection,
    },
    {
      id: 'IsRepairTimeNeedToBeAgreed',
      format: 'boolean',
      key: '##attribute#incident#uds_repairformneedtobeagreed',
      variants: [<CheckIcon />, <ClearIcon />],
      show: showEnteringSection,
    },
    {
      id: 'SendSms',
      format: 'boolean',
      key: '##attribute#incident#uds_needsendsms',
      variants: [<CheckIcon />, <ClearIcon />],
      header: {
        position: 'before',
        text: 'Seuranta',
      },
    },
    {
      id: 'SendEmail',
      format: 'boolean',
      key: '##attribute#incident#uds_needsendemail',
      variants: [<CheckIcon />, <ClearIcon />],
    },
    {
      id: 'Description',
      format: 'string',
      key: '',
      header: {
        position: 'before',
        text: 'Kuvaus',
      },
      renderFullItem: (commonCase) => (
        <li>
          <Description>
            {commonCase.Description && safeParseHtml(commonCase.Description)}
          </Description>
        </li>
      ),
    },
  ],
  taskInfo: [
    {
      id: 'Tasks',
      format: 'list',
      key: '',
      render: (commonCaseTask: CommonCaseTask, idx) => (
        <TaskInfo task={commonCaseTask} idx={idx} />
      ),
    },
  ],
};
